import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';
import { useGetStudents } from '../../hooks/students/useGetStudents';
import { useDeleteStudent } from '../../hooks/students/useDeleteStudent';
import { useDeleteAllStudents } from '../../hooks/students/useDeleteAllStudents';
import { useForceDeleteStudent } from '../../hooks/students/useForceDeleteStudent';
import { useForceDeleteAllStudents } from '../../hooks/students/useForceDeleteAllStudents';
import { useRestoreStudent } from '../../hooks/students/useRestoreStudent';
import { useRestoreAllStudents } from '../../hooks/students/useRestoreAllStudents';
import { usePermissions } from '../../auth/PermissionsContext';
import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import StudentsTableColumns from './Table/StudentsTableColumns';
import StudentsTableRow from './Table/StudentsTableRow';
import ModelShowStudent from './ModelShowStudent';

const ageOptions = Array.from({ length: 96 }, (_, i) => i + 5); // 5 to 100

const StudentsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterGender, setFilterGender] = useState('');
    const [filterAgeFrom, setFilterAgeFrom] = useState('');
    const [filterAgeTo, setFilterAgeTo] = useState('');
    const [openView, setOpenView] = useState(false);
    const [viewId, setViewId] = useState(null);

    const perms = useMemo(() => ({
        list: has('students_list'), create: has('students_create'), edit: has('students_edit'),
        show: has('students_show'), delete: has('students_delete'), archive_list: has('students_archive_list'),
        restore: has('students_restore'), force_delete: has('students_force_delete'),
    }), [has]);

    const { deleteStudent, isDelete } = useDeleteStudent();
    const { deleteAllStudents, isDelete: isDeletingAll } = useDeleteAllStudents();
    const { forceDeleteStudent } = useForceDeleteStudent();
    const { forceDeleteAllStudents } = useForceDeleteAllStudents();
    const { restoreStudent } = useRestoreStudent();
    const { restoreAllStudents } = useRestoreAllStudents();

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/students', deleteSingle: deleteStudent, deleteAll: deleteAllStudents,
        forceDeleteSingle: forceDeleteStudent, forceDeleteAll: forceDeleteAllStudents,
        restoreSingle: restoreStudent, restoreAll: restoreAllStudents, isArchive,
    });

    const handleView = () => { if (selectedItem) { setViewId(selectedItem.id); setOpenView(true); } handleMenuClose(); };

    const filters = useMemo(() => ({
        search: search || undefined, sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        gender: filterGender || undefined,
        age_from: filterAgeFrom || undefined,
        age_to: filterAgeTo || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterGender, filterAgeFrom, filterAgeTo, isArchive]);

    const { isLoading, error, students, meta } = useGetStudents(page, perPage, filters);

    const rtlSelectSx = isArabic ? {
        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
        '& .MuiInputLabel-shrink': { right: 32 },
        '& .MuiSelect-icon': { left: 7, right: 'unset' },
        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
    } : {};

    const renderRow = (params) => (
        <StudentsTableRow key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={students.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen} />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" value={filterGender}
                    onChange={(e) => setFilterGender(e.target.value)}
                    label={isArabic ? 'النوع' : 'Gender'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 140, ...rtlSelectSx }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    <MenuItem value="male">{isArabic ? 'ذكر' : 'Male'}</MenuItem>
                    <MenuItem value="female">{isArabic ? 'أنثى' : 'Female'}</MenuItem>
                </TextField>

                <TextField select size="small" value={filterAgeFrom}
                    onChange={(e) => {
                        setFilterAgeFrom(e.target.value);
                        if (filterAgeTo && e.target.value && Number(e.target.value) > Number(filterAgeTo)) {
                            setFilterAgeTo(e.target.value);
                        }
                    }}
                    label={isArabic ? 'العمر من' : 'Age From'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 120, ...rtlSelectSx }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300, direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {ageOptions.map((a) => (<MenuItem key={a} value={a}>{a}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterAgeTo}
                    onChange={(e) => setFilterAgeTo(e.target.value)}
                    label={isArabic ? 'العمر إلى' : 'Age To'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 120, ...rtlSelectSx }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300, direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {ageOptions.filter((a) => !filterAgeFrom || a >= Number(filterAgeFrom)).map((a) => (
                        <MenuItem key={a} value={a}>{a}</MenuItem>
                    ))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/students/archive" />}
                    {isArchive && perms.list && <ArchiveButton to="/students" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف الطلاب' : 'الطلاب') : (isArchive ? 'Students Archive' : 'Students')}
                subtitle={isArabic ? (isArchive ? 'إدارة الطلاب المحذوفين' : 'إدارة الطلاب') : (isArchive ? 'Manage deleted students' : 'Manage students')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة طالب' : 'Add Student'}
                onAddClick={() => navigate('/students/create')} addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error} items={students} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, students)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete} onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={StudentsTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuView={perms.show ? handleView : undefined}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit} menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore} isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا يوجد طلاب في الأرشيف' : 'لا يوجد طلاب'}
                emptyLabelEn={isArchive ? 'No archived students' : 'No students'}
                loadErrorAr="حدث خطأ أثناء تحميل الطلاب" loadErrorEn="Failed to load students"
            />
            {openView && <ModelShowStudent studentId={viewId} open={openView} onClose={() => setOpenView(false)} />}
        </>
    );
};

export default StudentsTable;
